import {
  CHARACTER_BLURBS,
  CHARACTERS,
  DEFAULT_CONFIG,
  HEALTH_PRESETS,
  SPECIES_ORDER,
  THEMES,
  THEME_IDS,
  TWEAK_BOUNDS,
  TWEAK_DEFAULTS,
  UNLIMITED_AMMO,
  WEAPONS,
  WEAPON_IDS,
  ROOM_CODE_ALPHABET,
  ROOM_CODE_LEN,
  clampTweaks,
  normalizeRoomCode,
  randomRoomCode,
  type AimLine,
  type Entity,
  type MatchmakingMsg,
  type PublicMatchConfig,
  type RoomStateMsg,
  type SpeciesId,
  type ThemeId,
  type WeaponId,
  type WeaponTweaks,
} from '@cuboom/shared';
import { SQUAD_SIZE, loadSquad, saveSquad } from '../game/squad.js';
import { loadWeaponTweaks, saveWeaponTweaks } from '../game/tweaks.js';
import { drawCharacter } from '../art/character.js';
import { paintMapPreview } from '../render/mapPreview.js';
import {
  enterFullscreenIfUseful,
  fullscreenSupported,
  isFullscreen,
  onFullscreenChange,
  toggleFullscreen,
} from './fullscreen.js';

/**
 * Text-list menus (per the mobile spec: menus are tappable text, gameplay is
 * icons). Screens live here:
 *   menu   — IN A MATCH: Resume / Leave match, nothing else. Otherwise:
 *            Squad / Setup & tuning / Music / Play vs CPU / Find match /
 *            Play with a friend / How to play
 *   squad  — pick your team's soldiers
 *   setup  — the ONE place a match is configured: map, soldiers' health, aim
 *            line, ammo, power and jump. Feeds rooms you host and CPU matches.
 *   guide  — the controls & rules, so the play screen can stay clean
 *   online — name + the room code (type a friend's to join, or roll one to
 *            create). Invite codes only: finding a stranger is not this
 *            screen's job.
 *   match  — Find match: wait for strangers (humans only). No invite codes
 *            here either.
 *   room   — the lobby: code, players, ready check, host config
 */
type Screen = 'menu' | 'squad' | 'setup' | 'guide' | 'online' | 'match' | 'room';

export interface LobbyCbs {
  /** Back into the match currently behind the overlay. */
  onResume(): void;
  /** Abandon the live match for good — closes it (forfeiting online) and lands on the menu. */
  onLeaveMatch(): void;
  onPlayCpu(): void;
  /** Background-music power state — the menu row's label. */
  musicOn(): boolean;
  /** Flip background music; returns the new state. */
  onToggleMusic(): boolean;
  /** The online screen was opened — make sure a socket exists. */
  onOpenOnline(name: string): void;
  /** Find match: wait for strangers with no invite code (humans only). */
  onFindMatch(name: string): void;
  onCancelMatch(): void;
  /** Create an invite room from the saved setup; `code` undefined = server picks. */
  onCreate(config: PublicMatchConfig, code?: string): void;
  onJoin(code: string): void;
  onReady(ready: boolean): void;
  onConfig(patch: Partial<PublicMatchConfig>): void;
  /** The menu's name box changed — persist it for every mode to read. */
  onNameChange(name: string): void;
  onLeave(): void;
}

const OVERLAY =
  'position:absolute;inset:0;display:none;flex-direction:column;align-items:center;justify-content:center;' +
  'gap:10px;background:linear-gradient(180deg,rgba(8,12,20,.88),rgba(8,12,20,.96));z-index:20;color:#fff;' +
  'font-family:system-ui;padding:20px;box-sizing:border-box;overflow-y:auto';

const BIG_BTN =
  'min-width:240px;padding:14px 24px;border:1px solid rgba(255,255,255,.32);border-radius:12px;' +
  'background:rgba(255,255,255,.09);color:#fff;font:600 16px/1.2 system-ui;cursor:pointer;' +
  'touch-action:manipulation;transition:background .1s';

const ROW_BTN =
  'display:block;width:100%;text-align:left;padding:10px 12px;border:1px solid rgba(255,255,255,.22);' +
  'border-radius:9px;background:rgba(255,255,255,.06);color:#fff;font:500 14px/1.3 system-ui;' +
  'cursor:pointer;touch-action:manipulation';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  css: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.style.cssText = css;
  if (text !== undefined) node.textContent = text;
  return node;
}

function mkBtn(label: string, css: string, fn: () => void): HTMLButtonElement {
  const b = el('button', css, label);
  b.type = 'button';
  b.addEventListener('click', () => {
    fn();
    b.blur();
  });
  b.addEventListener('pointerenter', () => (b.style.background = 'rgba(255,255,255,.19)'));
  b.addEventListener('pointerleave', () => (b.style.background = 'rgba(255,255,255,.09)'));
  return b;
}

// --- reusable setup widgets ------------------------------------------------
// These live at module scope so the setup screen owns them once; the setup
// screen is now the single place a match is configured (map, ammo, power,
// jump, health, room code), and it feeds both online rooms and Play vs CPU.

const STEP_BTN =
  'min-width:30px;padding:3px 0;border:1px solid rgba(255,255,255,.3);border-radius:8px;' +
  'background:rgba(255,255,255,.09);color:#fff;font:700 15px/1 system-ui;cursor:pointer;' +
  'touch-action:manipulation';

const PRESET_BTN =
  'flex:1;padding:7px 0;border:1px solid rgba(255,255,255,.28);border-radius:8px;' +
  'background:rgba(255,255,255,.07);color:#fff;font:600 13px system-ui;cursor:pointer;touch-action:manipulation';
const PRESET_ON = ';border-color:#ffd76a;background:rgba(255,215,106,.16)';
const PRESET_OFF = ';border-color:rgba(255,255,255,.28);background:rgba(255,255,255,.07)';

const CODE_INPUT_CSS =
  'flex:1;min-width:0;box-sizing:border-box;padding:10px 12px;border-radius:9px;border:1px solid rgba(255,255,255,.25);' +
  'background:rgba(255,255,255,.08);color:#fff;font:700 20px/1 system-ui;letter-spacing:.26em;text-align:center;' +
  'text-transform:uppercase;outline:none';

const REROLL_BTN =
  'min-width:52px;padding:10px 0;border:1px solid rgba(255,255,255,.3);border-radius:9px;' +
  'background:rgba(255,255,255,.09);color:#fff;font:600 17px/1 system-ui;cursor:pointer;touch-action:manipulation';

const fmtAmmo = (n: number): string => (n >= UNLIMITED_AMMO ? '∞' : String(n));

/** Scalar (non-per-weapon) tuning knobs that are stored as plain numbers. */
type ScalarKnob = 'chiliRounds' | 'lemonRounds' | 'beeLifeS' | 'beeStingDmg' | 'jumpPower' | 'startHealth';

/** Set a scalar knob, deleting it when it matches stock (untuned = no entry). */
function setScalar(tweaks: WeaponTweaks, k: ScalarKnob, v: number, def: number, onChange: () => void): void {
  if (Math.abs(v - def) < 1e-9) delete tweaks[k];
  else tweaks[k] = v;
  onChange();
}

/**
 * One weapon's magazine stepper. `get` is a thunk, not the object itself:
 * saving re-clamps into a fresh copy, so a widget that held the old reference
 * would keep editing a dead one. Mutates `ammo` in place (delete-when-stock,
 * so an untuned weapon holds no entry) and calls `onChange`. The wrap ladder
 * matches the reference UI: − from 0 → ∞, − from ∞ → the 20 cap, and + walks
 * the other way (20 → ∞ → 1).
 */
function ammoStepperRow(id: WeaponId, get: () => WeaponTweaks, onChange: () => void): HTMLElement {
  const stock = WEAPONS[id].ammo;
  const cur = (): number => get().ammo?.[id] ?? stock;
  const set = (n: number): void => {
    const tweaks = get();
    tweaks.ammo ??= {};
    if (n === stock) delete tweaks.ammo[id];
    else tweaks.ammo[id] = n;
    if (!Object.keys(tweaks.ammo).length) delete tweaks.ammo;
    onChange();
  };
  const wrap = el(
    'div',
    'padding:6px 8px;border:1px solid rgba(255,255,255,.14);border-radius:9px;background:rgba(255,255,255,.05)',
  );
  wrap.append(
    el('div', 'font:600 12px system-ui;white-space:nowrap;overflow:hidden;text-overflow:ellipsis', WEAPONS[id].name),
  );
  const bar = el('div', 'display:flex;align-items:center;gap:6px;margin-top:4px');
  const valEl = el('span', 'font:700 13px system-ui;color:#ffd76a;flex:1;text-align:center', fmtAmmo(cur()));
  const minus = mkBtn('−', STEP_BTN, () => {
    const n = cur();
    set(n >= UNLIMITED_AMMO ? TWEAK_BOUNDS.ammoHi : n <= TWEAK_BOUNDS.ammoLo ? UNLIMITED_AMMO : n - 1);
    valEl.textContent = fmtAmmo(cur());
  });
  const plus = mkBtn('+', STEP_BTN, () => {
    const n = cur();
    set(n >= UNLIMITED_AMMO ? 1 : n >= TWEAK_BOUNDS.ammoHi ? UNLIMITED_AMMO : n + 1);
    valEl.textContent = fmtAmmo(cur());
  });
  minus.title = 'Less ammo — 0 steps up to ∞';
  plus.title = 'More ammo — the 20 cap steps to ∞';
  bar.append(minus, valEl, plus);
  wrap.append(bar);
  return wrap;
}

/** Slider row: label left, live value right, full-width range under. */
function sliderRow(
  label: string,
  min: number,
  max: number,
  step: number,
  value: number,
  fmt: (v: number) => string,
  onInput: (v: number) => void,
): HTMLElement {
  const wrap = el(
    'div',
    'padding:7px 10px;border:1px solid rgba(255,255,255,.14);border-radius:9px;background:rgba(255,255,255,.05)',
  );
  const head = el('div', 'display:flex;justify-content:space-between;align-items:baseline;gap:8px');
  head.append(el('span', 'font:600 13px system-ui', label));
  const valEl = el('span', 'font:700 12px system-ui;color:#ffd76a;white-space:nowrap', fmt(value));
  head.append(valEl);
  const range = el('input', 'width:100%;margin:6px 0 0;accent-color:#ffd76a;touch-action:manipulation');
  range.type = 'range';
  range.min = String(min);
  range.max = String(max);
  range.step = String(step);
  range.value = String(value);
  range.addEventListener('input', () => {
    const v = Number(range.value);
    valEl.textContent = fmt(v);
    onInput(v);
  });
  wrap.append(head, range);
  return wrap;
}

/**
 * Tappable choice row — one highlighted option out of a fixed set. `current`
 * is a getter so the row repaints itself from live state after a pick.
 */
function choiceRow<T>(
  label: string,
  values: readonly T[],
  current: () => T,
  fmt: (v: T) => string,
  onPick: (v: T) => void,
): HTMLElement {
  const wrap = el(
    'div',
    'padding:7px 10px;border:1px solid rgba(255,255,255,.14);border-radius:9px;background:rgba(255,255,255,.05)',
  );
  wrap.append(el('div', 'font:600 13px system-ui', label));
  const bar = el('div', 'display:flex;gap:6px;margin-top:6px');
  // Hand-rolled rather than `mkBtn`: mkBtn's hover handlers hard-set the
  // background, which would erase the "selected" highlight on pointer-leave.
  const btns = new Map<T, HTMLButtonElement>();
  for (const v of values) {
    const b = el('button', PRESET_BTN, fmt(v));
    b.type = 'button';
    b.addEventListener('click', () => {
      onPick(v);
      paint();
      b.blur();
    });
    b.addEventListener('pointerenter', () => (b.style.background = 'rgba(255,255,255,.19)'));
    b.addEventListener('pointerleave', () => paint());
    btns.set(v, b);
    bar.append(b);
  }
  const paint = (): void => {
    const now = current();
    for (const [v, b] of btns) b.style.cssText = PRESET_BTN + (v === now ? PRESET_ON : PRESET_OFF);
  };
  paint();
  wrap.append(bar);
  return wrap;
}

// --- map choice (persisted, applies to the next local match) ---------------

const LS_MAP = 'cuboom.map';
type MapChoice = ThemeId | 'random';

/** One-line flavor for the map list — why you'd pick this terrain. */
const MAP_BLURB: Record<ThemeId, string> = {
  meadow: 'watchtowers, hills and open ground',
  forest: 'stump platforms and a log bridge',
  desert: 'stepped buttes and hoodoos',
  snow: 'igloos you can stand on and blow up',
  city: 'streets, hollow buildings, rooftop tanks',
  canyon: 'a rock arch to fight on and under',
  highlands: 'paired stone keeps with floors',
  islands: 'rafts and stilt huts over the sea',
  harbor: 'two ships, piers and warehouse roofs',
  lagoon: 'turquoise keys, shallow bays and stilt huts',
  skyline: 'downtown towers, taller roofs, street lamps',
  neon: 'night district glowing magenta and cyan',
  candy: 'pastel hills of pink, mint and lavender',
  sunset: 'banded dusk mesas in coral and violet',
  void: 'near-black ridges under a star field',
  prism: 'terrain that shifts hue across the map',
  magma: 'basalt columns, hot speckle, ember sky',
  mint: 'soft teal plateaus, calm and clean',
  golden: 'amber prairie glowing in late light',
  orchid: 'violet dusk hollows with crystal seams',
  mono: 'black-and-white minimal, pure silhouette',
};

function loadMapChoice(): MapChoice {
  try {
    const v = localStorage.getItem(LS_MAP);
    if (v === 'random' || (v && THEMES[v as ThemeId])) return v as MapChoice;
  } catch {
    /* private mode — default is fine */
  }
  return 'random';
}

function saveMapChoice(v: MapChoice): void {
  try {
    localStorage.setItem(LS_MAP, v);
  } catch {
    /* ignore */
  }
}

/**
 * Aim-line assist: `normal` (default) cuts the dots at ~2/3 of the flight with
 * no landing spot; `extended` draws the full preview arc and its impact ring.
 * Storage is this player's preference for their OWN matches — inside a
 * room the config's `aimLine` wins, so everyone sees the same preview.
 */
export function loadAimLine(): AimLine {
  try {
    return localStorage.getItem('cuboom.aimLine') === 'extended' ? 'extended' : 'normal';
  } catch {
    return 'normal';
  }
}

function saveAimLine(v: AimLine): void {
  try {
    localStorage.setItem('cuboom.aimLine', v);
  } catch {
    /* ignore */
  }
}

/**
 * Live procedural portrait of a species — the squad picker shows the actual
 * character art, not a color dot. Static `t` keeps thumbs from breathing.
 */
function portrait(id: SpeciesId, size: number): HTMLCanvasElement {
  const spec = CHARACTERS[id];
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const cv = document.createElement('canvas');
  cv.width = Math.round(size * dpr);
  cv.height = Math.round(size * dpr);
  cv.style.width = `${size}px`;
  cv.style.height = `${size}px`;
  const ctx = cv.getContext('2d');
  if (!ctx || !spec) return cv;
  ctx.scale(dpr, dpr);
  const footX = size / 2;
  const footY = size - 2;
  // Scale down around the feet anchor so tall species (tree canopy, tractor
  // exhaust) fit inside the tile instead of clipping at the top edge.
  ctx.translate(footX, footY);
  ctx.scale(0.9, 0.9);
  ctx.translate(-footX, -footY);
  const e: Entity = {
    id: 0,
    teamId: 0,
    seat: 0,
    species: id,
    name: spec.name,
    accent: spec.accent,
    x: footX,
    y: footY,
    vx: 0,
    vy: 0,
    hp: spec.stats.maxHp,
    maxHp: spec.stats.maxHp,
    armor: spec.stats.armor,
    facing: 1,
    onGround: true,
    pose: 'idle',
    poseTimer: 0,
    alive: true,
    airTimeS: 0,
    maxFallSpeed: 0,
    stunTurns: 0,
    burningTurns: 0,
    drownTimerS: 0,
    lastBubbleTick: -999,
    lastHitTeamId: null,
    lastLineIdx: -1,
    lastLandSpeed: 0,
    suppressFall: false,
    script: null,
  };
  drawCharacter(ctx, e, spec, { active: false, aim: null, t: 0 });
  return cv;
}

export class Lobby {
  readonly root: HTMLDivElement;

  private readonly menuEl: HTMLDivElement;
  private readonly squadEl: HTMLDivElement;
  private readonly setupEl: HTMLDivElement;
  private readonly guideEl: HTMLDivElement;
  private readonly onlineEl: HTMLDivElement;
  private readonly matchEl: HTMLDivElement;
  private readonly roomEl: HTMLDivElement;
  private readonly errorEl: HTMLDivElement;
  private readonly statusEl: HTMLDivElement;
  private readonly nameInput: HTMLInputElement;
  /** The name + connection row — one field, parked at the top of the menu. */
  private readonly nameRow: HTMLDivElement;
  private readonly codeInput: HTMLInputElement;
  private readonly createBtn: HTMLButtonElement;
  private readonly joinBtn: HTMLButtonElement;
  private readonly findBtn: HTMLButtonElement;
  private readonly cancelMatchBtn: HTMLButtonElement;
  private readonly matchStatusEl: HTMLDivElement;
  private readonly matchSlotsEl: HTMLDivElement;
  private readonly matchNoteEl: HTMLDivElement;
  private readonly matchBox: HTMLDivElement;
  private readonly resumeBtn: HTMLButtonElement;
  private readonly leaveBtn: HTMLButtonElement;
  /** The full menu rows — hidden while a match is live (in-match = 2 options). */
  private readonly normalBox: HTMLDivElement;
  private readonly squadBtn: HTMLButtonElement;
  private readonly musicBtn: HTMLButtonElement;
  private readonly fsBtn: HTMLButtonElement;

  private screen: Screen = 'menu';
  private room: RoomStateMsg | null = null;
  private myId = '';
  private online = false;
  private canResume = false;
  /** True while setup was opened FROM the room — Back returns there, and room
   *  updates are stored quietly instead of yanking the screen back. */
  private setupFromRoom = false;
  /** Debounce handle for pushing room weapon tweaks (sliders fire per input). */
  private tweakPush: number | undefined;
  /** Last instant-match status pushed by the server, or null when idle. */
  private mm: MatchmakingMsg | null = null;
  /** Set from /config: this server offers instant match. */
  private mmEnabled = true;
  /** True from the moment "Find match" is tapped until a status arrives. */
  private searching = false;
  private squad: SpeciesId[] = loadSquad();
  private mapChoice: MapChoice = loadMapChoice();
  private tweaks: WeaponTweaks = loadWeaponTweaks() ?? {};

  constructor(parent: HTMLElement, private readonly cbs: LobbyCbs, storedName: string) {
    this.root = el('div', OVERLAY);

    // --- menu ---------------------------------------------------------------
    this.menuEl = el('div', 'display:none;flex-direction:column;align-items:center;gap:12px');
    const title = el(
      'h1',
      'margin:0 0 4px;font-size:clamp(30px,9vw,46px);letter-spacing:.06em;text-shadow:0 3px 16px rgba(0,0,0,.7)',
      'CuBoOm!',
    );
    this.resumeBtn = mkBtn('▶  Resume match', BIG_BTN, () => this.cbs.onResume());
    this.leaveBtn = mkBtn('✕  Leave match', BIG_BTN, () => this.cbs.onLeaveMatch());
    // (findBtn is created with the menu rows below — one button, two homes.)
    this.resumeBtn.style.display = 'none';
    this.leaveBtn.style.display = 'none';
    // Destructive — tinted so it reads as "this ends the match".
    this.leaveBtn.style.background = 'rgba(255,110,110,.14)';
    this.leaveBtn.style.borderColor = 'rgba(255,130,130,.5)';
    // Everything below the two match options. While a match is live it is
    // hidden, so the in-match menu is EXACTLY Resume + Leave match.
    this.normalBox = el('div', 'display:flex;flex-direction:column;align-items:center;gap:12px;width:100%');
    this.squadBtn = mkBtn(this.squadLabel(), BIG_BTN, () => this.show('squad'));
    // Background music power. The HUD button can flip it mid-match, so the
    // label re-reads on menu open.
    this.musicBtn = mkBtn(this.musicLabel(), BIG_BTN, () => {
      this.musicBtn.textContent = this.musicLabel(this.cbs.onToggleMusic());
    });
    // Fullscreen power — the menu face of the same toggle the HUD's ⛶ uses.
    // The label follows the real state (the HUD or Esc can flip it while the
    // menu is closed), and on platforms that refuse page fullscreen
    // (iPhone Safari) the row never appears at all.
    this.fsBtn = mkBtn(this.fsLabel(), BIG_BTN, () => toggleFullscreen());
    if (!fullscreenSupported()) this.fsBtn.style.display = 'none';
    onFullscreenChange(() => {
      this.fsBtn.textContent = this.fsLabel();
    });
    // Find match — the no-friends path. It sits above "Play with a friend"
    // (the invite-code flow) because it is the one-tap way into a game with
    // real people. It needs another human to be searching; CPUs are a separate,
    // deliberate button on that screen.
    const findBtn = mkBtn('⚡  Find match', BIG_BTN, () => this.startSearch());
    findBtn.style.background = 'rgba(120,200,255,.16)';
    findBtn.style.borderColor = 'rgba(150,215,255,.6)';

    this.normalBox.append(
      this.squadBtn,
      mkBtn('⚙  Setup & tuning', BIG_BTN, () => {
        // Entering from the menu is the STORAGE-backed setup (not a room's).
        // Re-seed: room edits never touch storage, so the fields could still
        // hold the last room's tuning.
        this.setupFromRoom = false;
        this.tweaks = loadWeaponTweaks() ?? {};
        this.mapChoice = loadMapChoice();
        this.show('setup');
      }),
      this.musicBtn,
      this.fsBtn,
      mkBtn('🎮  Play vs CPU', BIG_BTN, () => this.cbs.onPlayCpu()),
      findBtn,
      mkBtn('🌐  Play with a friend', BIG_BTN, () => this.openOnline()),
      mkBtn('📖  How to play', BIG_BTN, () => this.show('guide')),
    );
    this.menuEl.append(title, this.resumeBtn, this.leaveBtn, this.normalBox);

    // --- squad picker -------------------------------------------------------
    this.squadEl = el('div', 'display:none;flex-direction:column;align-items:center;gap:10px;width:min(460px,96vw)');

    // --- setup & tuning (map · aim · health · ammo · power · jump) ----------
    this.setupEl = el(
      'div',
      'display:none;flex-direction:column;align-items:stretch;gap:8px;width:min(460px,96vw);' +
        'max-height:88vh;overflow-y:auto;padding:4px 6px;box-sizing:border-box',
    );

    // --- how to play (the guides that used to sit on screen) ----------------
    this.guideEl = el(
      'div',
      'display:none;flex-direction:column;align-items:stretch;gap:10px;width:min(480px,96vw);' +
        'max-height:86vh;overflow-y:auto;padding:4px 6px;box-sizing:border-box',
    );
    this.buildGuide();

    // --- online (invite code) ----------------------------------------------
    this.onlineEl = el('div', 'display:none;flex-direction:column;align-items:center;gap:12px;width:min(340px,92vw)');
    const nameLabel = el('label', 'align-self:flex-start;font:600 12px system-ui;opacity:.75', 'Your name');
    this.nameInput = el(
      'input',
      'width:100%;box-sizing:border-box;padding:11px 12px;border-radius:9px;border:1px solid rgba(255,255,255,.25);' +
        'background:rgba(255,255,255,.08);color:#fff;font:15px system-ui;outline:none',
    );
    this.nameInput.value = storedName || '';
    this.nameInput.maxLength = 24;
    this.nameInput.placeholder = 'player'; // what every mode falls back to
    this.nameInput.setAttribute('autocomplete', 'nickname');
    // Persist as you type: create/join/find/reconnect all read the stored
    // name, so the box must be live before any of them fire.
    this.nameInput.addEventListener('input', () => {
      this.cbs.onNameChange(this.nameInput.value.trim());
    });
    this.nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.nameInput.blur(); // dismiss the mobile keyboard
    });

    this.statusEl = el('div', 'font:12px/1.4 system-ui;opacity:.75;min-height:17px;text-align:center', 'not connected');

    // One name field for the whole game, living on the MAIN MENU: type it
    // once, every mode (local, find match, friend room) uses it. It sits at
    // the top of the normal menu rows so the in-match menu — which hides
    // those rows — stays exactly Resume + Leave match.
    this.nameRow = el('div', 'display:flex;flex-direction:column;gap:7px;width:100%;max-width:340px');
    this.nameRow.append(nameLabel, this.nameInput, this.statusEl);
    this.normalBox.prepend(this.nameRow);

    // One field, two intents: type a friend's code and Join it, or type (or
    // roll) one and Create the room under it. Empty + Create = the server picks.
    const codeLabel = el('label', 'align-self:flex-start;font:600 12px system-ui;opacity:.75;margin-top:6px', 'Room code');
    this.codeInput = el('input', CODE_INPUT_CSS);
    this.codeInput.placeholder = 'ABCD';
    this.codeInput.maxLength = 8; // roomier than 4 so a pasted "JOIN-ABCD" still fits
    this.codeInput.autocapitalize = 'characters';
    this.codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.tryJoin();
    });
    // Excluded characters never reach the field: typing "1" or "O" is dropped
    // as it happens, so the code on screen is always a real one. Pasted text
    // (including "JOIN-ABCD") runs through the same filter.
    this.codeInput.addEventListener('input', () => {
      const clean = this.codeInput.value
        .toUpperCase()
        .split('')
        .filter((c) => ROOM_CODE_ALPHABET.includes(c))
        .join('')
        .slice(0, ROOM_CODE_LEN);
      if (this.codeInput.value !== clean) this.codeInput.value = clean;
    });
    const reroll = mkBtn('↻', REROLL_BTN, () => {
      this.codeInput.value = randomRoomCode();
      this.clearError();
    });
    reroll.title = 'Roll a code for the room you create';
    const codeBar = el('div', 'display:flex;gap:6px;width:100%');
    codeBar.append(this.codeInput, reroll);

    this.joinBtn = mkBtn('Join room', ROW_BTN, () => this.tryJoin());
    this.createBtn = mkBtn('Create room', ROW_BTN + ';text-align:center;font-weight:700', () => {
      if (!this.online) return this.showError('Connecting… try again in a moment.');
      // Validate here rather than letting the server reject the tap: a bad
      // code is a typo the player can fix on this screen.
      const code = this.roomCodeAttempt();
      if (code === null) return;
      // The match itself comes from the setup screen (map, health, ammo…).
      this.clearError();
      this.cbs.onCreate(this.buildConfig(), code || undefined);
    });

    // --- instant match panel (the Find match screen) ------------------------
    // Strangers search here — never on the invite-code screen: finding a
    // stranger and playing with a friend are different intents.
    const mmBox = el(
      'div',
      'display:flex;flex-direction:column;gap:7px;width:100%;padding:11px 12px;box-sizing:border-box;' +
        'border:1px solid rgba(150,215,255,.4);border-radius:11px;background:rgba(120,200,255,.08)',
    );
    mmBox.append(el('div', 'font:700 13px system-ui;color:#9fd8ff', '⚡  Find match — other players'));
    this.matchStatusEl = el('div', 'font:13px/1.45 system-ui;opacity:.9', 'Real people only — no CPUs, no invite code.');
    this.matchSlotsEl = el('div', 'display:flex;flex-direction:column;gap:4px');
    this.matchNoteEl = el(
      'div',
      'font:11px/1.4 system-ui;opacity:.6',
      'Humans only: you wait until enough people are searching. Server picks the map and rules.',
    );
    this.findBtn = mkBtn('Find match', ROW_BTN + ';text-align:center;font-weight:700;background:rgba(120,200,255,.2)', () =>
      this.startSearch(),
    );
    this.cancelMatchBtn = mkBtn('✕  Cancel search', ROW_BTN + ';text-align:center;display:none', () => {
      this.cbs.onCancelMatch();
      this.searching = false;
      this.mm = null;
      this.renderMatchmaking();
      this.show('menu'); // cancelling leaves the screen — the menu is home
    });
    mmBox.append(this.matchStatusEl, this.matchSlotsEl, this.matchNoteEl, this.findBtn, this.cancelMatchBtn);
    this.matchBox = mmBox;

    // --- find match screen --------------------------------------------------
    // No separate Back button: leaving is the cancel button's job (it returns
    // to the menu whether a search is running or not).
    this.matchEl = el('div', 'display:none;flex-direction:column;align-items:center;gap:12px;width:min(340px,92vw)');
    this.matchEl.append(mmBox);

    this.onlineEl.append(
      el('div', 'align-self:stretch;height:1px;background:rgba(255,255,255,.14);margin:4px 0'),
      codeLabel,
      codeBar,
      el(
        'div',
        'width:100%;font:11px/1.4 system-ui;opacity:.6;text-align:center',
        'Type a friend’s code and Join it — or type / roll one and Create a room with it. ' +
          'Leave it empty and the server picks.',
      ),
      this.joinBtn,
      this.createBtn,
      mkBtn('← Back', ROW_BTN, () => this.show('menu')),
    );

    // --- room ---------------------------------------------------------------
    this.roomEl = el('div', 'display:none;flex-direction:column;gap:10px;width:min(380px,94vw)');

    this.errorEl = el('div', 'display:none;max-width:min(380px,94vw);padding:9px 12px;border-radius:9px;' +
      'background:rgba(200,60,60,.22);border:1px solid rgba(255,120,120,.5);font:13px/1.4 system-ui;text-align:center');

    this.root.append(this.errorEl, this.menuEl, this.squadEl, this.setupEl, this.guideEl, this.onlineEl, this.matchEl, this.roomEl);
    parent.appendChild(this.root);

    this.setOnline(false);
    this.show('menu');
  }

  // --- public API ----------------------------------------------------------

  /**
   * The menu has two shapes. While a match is live (`canResume`) it is ONLY
   * Resume match + Leave match — nothing else is reachable mid-match.
   * Otherwise it is the full list.
   */
  showMenu(canResume: boolean): void {
    this.canResume = canResume;
    this.resumeBtn.style.display = canResume ? 'block' : 'none';
    this.leaveBtn.style.display = canResume ? 'block' : 'none';
    this.normalBox.style.display = canResume ? 'none' : 'flex';
    this.show('menu');
  }

  hide(): void {
    this.root.style.display = 'none';
    this.errorEl.style.display = 'none';
  }

  get visible(): boolean {
    return this.root.style.display !== 'none';
  }

  setIdentity(playerId: string): void {
    this.myId = playerId;
  }

  /** Socket handshake finished (or dropped). */
  setOnline(online: boolean, detail?: string): void {
    this.online = online;
    this.createBtn.disabled = !online;
    this.joinBtn.disabled = !online;
    this.createBtn.style.opacity = online ? '1' : '0.4';
    this.joinBtn.style.opacity = online ? '1' : '0.4';
    this.statusEl.textContent = online ? '● connected' : `○ ${detail ?? 'not connected'}`;
    this.statusEl.style.color = online ? '#7ee08a' : '';
    if (!online) {
      // A dropped socket can't be in a search any more.
      this.mm = null;
      this.searching = false;
    }
    this.renderMatchmaking();
  }

  /** The server told us instant match is available (or not) — see /config. */
  setMatchmakingEnabled(on: boolean): void {
    this.mmEnabled = on;
    this.renderMatchmaking();
  }

  /** A fresh status push from the server while queued. */
  setMatchmaking(msg: MatchmakingMsg | null): void {
    this.mm = msg;
    // The server decides whether *we* are still in line: a status push also
    // reaches us after cancelling (someone else is still waiting), and that
    // must not start our spinner again.
    this.searching = msg ? msg.youQueued : false;
    this.renderMatchmaking();
  }

  /**
   * Find match joins the human queue of strangers. Public: the result screen's
   * Rematch lands the player back on this screen with the search already
   * running — the queue must never be invisible behind a result screen.
   */
  startSearch(): void {
    // The tap that starts the search is a live user gesture — the phone's
    // chance to go fullscreen before the queue (the match start itself may
    // come minutes later, with no gesture left to spend).
    enterFullscreenIfUseful();
    this.clearError();
    this.searching = true;
    this.mm = null;
    this.show('match'); // strangers search on their own screen, not with friends
    this.renderMatchmaking();
    this.cbs.onFindMatch(this.nameInput.value.trim() || 'player');
  }

  private renderMatchmaking(): void {
    if (!this.matchBox) return;
    // A status push only means "someone is waiting on this server"; our own
    // spinner follows `youQueued` (plus the optimistic state before the reply).
    const searching = this.searching || !!this.mm?.youQueued;
    const queued = this.mm?.queued ?? (searching ? 1 : 0);
    const seats = this.mm?.seats ?? 2;
    const needed = this.mm?.needed ?? Math.max(0, seats - queued);

    this.matchStatusEl.textContent = !searching
      ? 'Real people only — no CPUs, no invite code.'
      : needed > 0
        ? `Searching for players… ${needed} more needed`
        : 'Match found — starting…';
    this.findBtn.style.display = searching ? 'none' : 'block';
    // The one way out of this screen: stop the search (if any) and go home.
    // Always visible — a dropped connection hides Find match, and the player
    // must still be able to leave.
    this.cancelMatchBtn.style.display = 'block';
    this.cancelMatchBtn.textContent = searching ? '✕  Cancel search' : '✕  Back to menu';
    const canSearch = this.online && this.mmEnabled;
    this.findBtn.disabled = !canSearch;
    this.findBtn.style.opacity = canSearch ? '1' : '0.4';
    this.findBtn.textContent = this.mmEnabled ? 'Find match' : 'Find match is off on this server';

    this.matchSlotsEl.replaceChildren();
    if (searching && seats) {
      // One row per human seat: filled for those already waiting, open for the
      // ones still needed. There is no CPU row — this queue never seats one.
      for (let i = 0; i < seats; i++) {
        const filled = i < queued;
        const row = el('div', 'display:flex;align-items:center;gap:7px;font:12px system-ui;padding:3px 0');
        row.append(el('span', 'opacity:.5;width:44px', `Seat ${i + 1}`));
        row.append(
          el('span', filled ? 'color:#9fd8ff' : 'opacity:.45', filled ? '● searching' : '○ open — waiting'),
        );
        this.matchSlotsEl.append(row);
      }
      this.matchNoteEl.textContent =
        'Humans only. The match starts once every seat is filled; map and rules come from the server.';
    } else {
      this.matchNoteEl.textContent =
        'Humans only: you wait until enough people are searching. Server picks the map and rules.';
    }
  }

  setRoom(msg: RoomStateMsg): void {
    this.room = msg;
    this.show('room');
  }

  /**
   * Take a room update WITHOUT opening the room screen. A room_state about a
   * match that is running (or a result screen already up) is bookkeeping —
   * yanking the lobby open over the match is never what the player wants.
   */
  setRoomQuiet(msg: RoomStateMsg): void {
    this.room = msg;
  }

  /** Show the last known room state right away (the win screen's OK uses it). */
  openRoom(): void {
    if (this.room) this.show('room');
  }

  /**
   * True while the room's Setup & tuning is open — the caller must store
   * room updates quietly so the host's own config echo (or the friend's
   * ready tap) doesn't yank them back to the room screen mid-edit.
   */
  get inRoomSetup(): boolean {
    return this.setupFromRoom && this.screen === 'setup';
  }

  clearRoom(): void {
    // A stranger match came from the menu; an invite room from the friend
    // screen. Go back to where the player actually started.
    const wasInstant = this.room?.kind === 'instant';
    this.room = null;
    this.setupFromRoom = false;
    if (this.screen === 'room') this.show(wasInstant ? 'menu' : this.online ? 'online' : 'menu');
  }

  showError(text: string): void {
    this.errorEl.textContent = text;
    this.errorEl.style.display = 'block';
  }

  clearError(): void {
    this.errorEl.style.display = 'none';
  }

  // --- internals -----------------------------------------------------------

  private show(screen: Screen): void {
    this.screen = screen;
    this.root.style.display = 'flex';
    this.menuEl.style.display = screen === 'menu' ? 'flex' : 'none';
    this.squadEl.style.display = screen === 'squad' ? 'flex' : 'none';
    this.setupEl.style.display = screen === 'setup' ? 'flex' : 'none';
    this.guideEl.style.display = screen === 'guide' ? 'flex' : 'none';
    this.onlineEl.style.display = screen === 'online' ? 'flex' : 'none';
    this.matchEl.style.display = screen === 'match' ? 'flex' : 'none';
    this.roomEl.style.display = screen === 'room' ? 'flex' : 'none';
    if (screen === 'room') this.renderRoom();
    if (screen === 'squad') this.renderSquad();
    if (screen === 'setup') this.renderSetup();
    if (screen === 'menu') {
      this.squadBtn.textContent = this.squadLabel();
      this.musicBtn.textContent = this.musicLabel(); // the HUD button may have flipped it
    }
  }

  /** Map pick for the next local match — main resolves 'random' at start. */
  get chosenMap(): MapChoice {
    return this.mapChoice;
  }

  private musicLabel(on = this.cbs.musicOn()): string {
    return `🎵  Music: ${on ? 'On' : 'Off'}`;
  }

  /** Two-way fullscreen row: enter when windowed, leave when fullscreen. */
  private fsLabel(): string {
    return isFullscreen() ? '⛶  Exit full screen' : '⛶  Full screen';
  }

  /**
   * Open Setup & tuning on THIS ROOM's config (host only, from the room's ⚙
   * row). The widgets then edit a copy seeded from the room and every save
   * pushes `set_config` instead of writing storage — the friend's room screen
   * follows the broadcasts, and Back returns here.
   */
  private openSetupFromRoom(): void {
    const cfg = this.room?.config;
    if (!cfg) return;
    this.setupFromRoom = true;
    this.tweaks = clampTweaks(cfg.weaponTweaks) ?? {};
    this.mapChoice = cfg.theme; // a live room always carries a concrete map
    this.show('setup');
  }

  /**
   * Ship the current tweaks to the room's server config. Sliders fire per
   * input event, so the push is debounced; `flush` sends immediately (Back,
   * Reset-to-stock) so nothing is lost to the next screen change.
   */
  private pushRoomTweaks(flush = false): void {
    window.clearTimeout(this.tweakPush);
    const send = (): void => {
      // `{}` means "stock": the server's clamp turns an empty object into a
      // cleared weaponTweaks, which is exactly what a reset should do.
      this.cbs.onConfig({ weaponTweaks: clampTweaks(this.tweaks) ?? {} });
    };
    if (flush) send();
    else this.tweakPush = window.setTimeout(send, 200);
  }

  /**
   * The setup screen — the ONE place a match is configured: the map, the
   * soldiers' starting health, per-weapon magazines and power, jump strength,
   * and the invitation code a room is created with. From the MENU it writes
   * through to storage as you tap (there is no draft copy), so `Play vs CPU`
   * and `Create room` read the same values and can never drift apart. From a
   * ROOM it edits that room's config instead — pushed live, storage untouched
   * (see `openSetupFromRoom`).
   */
  private renderSetup(): void {
    this.setupEl.replaceChildren();
    this.setupEl.append(
      el('h2', 'margin:10px 0 0;font-size:22px;letter-spacing:.03em;align-self:center', 'Setup & tuning'),
      el(
        'p',
        'margin:0;opacity:.65;font-size:13px;text-align:center;max-width:44ch',
        this.setupFromRoom
          ? 'What THIS room will run: map, health, ammo, power and jump. Changes go to the room ' +
            'as you go — your friend sees them too.'
          : 'What your next match runs: map, health, ammo, power and jump — for the CPU and for ' +
            'a room you host with friends. Saved as you go.',
      ),
    );

    const save = (): void => {
      const clean = clampTweaks(this.tweaks);
      this.tweaks = clean ?? {};
      // In a room the tuning belongs to the ROOM: push it, don't touch the
      // personal defaults the menu setup owns.
      if (this.setupFromRoom) this.pushRoomTweaks();
      else saveWeaponTweaks(clean);
    };

    const sectionLabel = (text: string, first = false): HTMLElement =>
      el(
        'div',
        'font:700 11px system-ui;opacity:.55;letter-spacing:.14em;text-transform:uppercase' +
          (first ? '' : ';margin-top:4px'),
        text,
      );

    // --- map ---------------------------------------------------------------
    // A live preview over a grouped chip grid. Repainted in place (not
    // re-rendered) so tapping a chip never scrolls the screen back to the top
    // or drops focus from the code field.
    const mapList = el('div', 'display:flex;flex-direction:column;gap:6px');
    const preview = el(
      'canvas',
      'display:block;width:100%;height:132px;border-radius:10px;background:rgba(0,0,0,.25)',
    );
    const caption = el('div', 'font:12px/1.45 system-ui;opacity:.75;min-height:34px');
    // 'random' previews a concrete roll — re-rolled on every tap so the dice
    // chip always shows the surprise you just asked for.
    let randomPreviewId: ThemeId = THEME_IDS[Math.floor(Math.random() * THEME_IDS.length)];
    const chips: { key: MapChoice; btn: HTMLButtonElement }[] = [];
    const previewId = (): ThemeId => (this.mapChoice === 'random' ? randomPreviewId : this.mapChoice);

    const paintMaps = (): void => {
      for (const c of chips) {
        const on = this.mapChoice === c.key;
        c.btn.style.borderColor = on ? '#ffd76a' : 'rgba(255,255,255,.22)';
        c.btn.style.background = on ? 'rgba(255,215,106,.14)' : 'rgba(255,255,255,.06)';
      }
      const id = previewId();
      paintMapPreview(preview, id);
      caption.textContent =
        this.mapChoice === 'random'
          ? `🎲 Random — a surprise theme every match (showing: ${THEMES[id].label} — ${MAP_BLURB[id]})`
          : `● ${THEMES[id].label} — ${MAP_BLURB[id]}`;
    };
    const addChip = (row: HTMLElement, key: MapChoice, label: string): void => {
      const btn = el(
        'button',
        'flex:1 1 84px;min-width:84px;display:flex;flex-direction:column;gap:5px;' +
          'padding:7px 8px 8px;border:1px solid rgba(255,255,255,.22);border-radius:9px;' +
          'background:rgba(255,255,255,.06);color:#fff;cursor:pointer;touch-action:manipulation;text-align:left',
      );
      btn.type = 'button';
      const theme = key === 'random' ? undefined : THEMES[key];
      const swatch = el(
        'div',
        'height:8px;border-radius:4px;background:' +
          (theme
            ? `linear-gradient(90deg,${theme.skyTop},${theme.grass} 55%,${theme.water})`
            : 'linear-gradient(90deg,#2e7dff,#7d5fff,#ff5fa2)'),
      );
      btn.append(swatch, el('span', 'font:500 10px/1.2 system-ui;overflow:hidden;text-overflow:ellipsis', label));
      btn.addEventListener('click', () => {
        if (key === 'random') {
          randomPreviewId = THEME_IDS[Math.floor(Math.random() * THEME_IDS.length)];
        }
        if (this.setupFromRoom) {
          // A room runs a concrete map: roll 'random' right here, then push
          // the pick so both seats see it (the highlight lands on what rolled).
          const picked = key === 'random' ? randomPreviewId : key;
          this.mapChoice = picked;
          this.cbs.onConfig({ theme: picked });
        } else {
          this.mapChoice = key;
          saveMapChoice(key);
        }
        paintMaps();
        btn.blur();
      });
      btn.addEventListener('pointerenter', () => (btn.style.background = 'rgba(255,255,255,.16)'));
      btn.addEventListener('pointerleave', paintMaps);
      chips.push({ key, btn });
      row.append(btn);
    };
    const chipRow = (): HTMLElement => el('div', 'display:flex;flex-wrap:wrap;gap:6px');

    mapList.append(preview, caption, sectionLabel('Random'));
    const randomRow = chipRow();
    addChip(randomRow, 'random', '🎲 Random');
    mapList.append(randomRow);
    const GROUPS: { id: 'nature' | 'city' | 'abstract'; label: string }[] = [
      { id: 'nature', label: 'Nature' },
      { id: 'city', label: 'City' },
      { id: 'abstract', label: 'Abstract' },
    ];
    for (const g of GROUPS) {
      const ids = THEME_IDS.filter((id) => THEMES[id].group === g.id);
      if (!ids.length) continue;
      mapList.append(sectionLabel(g.label));
      const row = chipRow();
      for (const id of ids) addChip(row, id, THEMES[id].label);
      mapList.append(row);
    }
    paintMaps();
    // The canvas measures itself on first paint — this block runs before
    // setupEl.append, so re-paint once the layout exists.
    requestAnimationFrame(() => paintMaps());

    // --- health ------------------------------------------------------------
    // A preset ladder, not a slider: the whole pacing of a match hangs off
    // this number, and 137 HP is nobody's idea of a good time.
    const healthList = el('div', 'display:flex;flex-direction:column;gap:6px');
    healthList.append(
      choiceRow(
        'Starting health — every soldier, both sides',
        HEALTH_PRESETS,
        () => this.tweaks.startHealth ?? TWEAK_DEFAULTS.startHealth,
        (v) => `${v} HP`,
        (v) => setScalar(this.tweaks, 'startHealth', v, TWEAK_DEFAULTS.startHealth, save),
      ),
    );

    // --- aiming ------------------------------------------------------------
    // How much the preview arc gives away. From the menu the renderer reads
    // storage, so a pick takes effect on the very next shot; from a room the
    // pick rides the room config to every seat instead.
    const aimList = el('div', 'display:flex;flex-direction:column;gap:6px');
    const aimRow = choiceRow<AimLine>(
      'Aim line — what the shot preview shows',
      ['normal', 'extended'],
      () => (this.setupFromRoom ? (this.room?.config.aimLine ?? 'normal') : loadAimLine()),
      (v) => (v === 'normal' ? 'Normal' : 'Extended'),
      (v) => (this.setupFromRoom ? this.cbs.onConfig({ aimLine: v }) : saveAimLine(v)),
    );
    aimRow.append(
      el(
        'div',
        'margin-top:5px;font:11px/1.4 system-ui;opacity:.6',
        'Extended draws the full arc and the landing ring; Normal stops the dots early and leaves ' +
          'you to guess.',
      ),
    );
    aimList.append(aimRow);

    // --- left column: ammo steppers ---------------------------------------
    // Counts, not sliders: − from 0 goes to ∞, − from ∞ lands on the 20 cap,
    // and + walks the other way (20 → ∞ → 1). Setting the stock count back
    // deletes the override, so a weapon on its default magazine holds no entry.
    const ammoList = el('div', 'display:flex;flex-direction:column;gap:6px');
    for (const id of WEAPON_IDS) ammoList.append(ammoStepperRow(id, () => this.tweaks, save));

    // --- right column: the sliders ----------------------------------------
    const powerList = el('div', 'display:flex;flex-direction:column;gap:6px');
    for (const id of WEAPON_IDS) {
      const cur = this.tweaks.dmg?.[id] ?? 1;
      powerList.append(
        sliderRow(
          WEAPONS[id].name,
          TWEAK_BOUNDS.dmgLo * 100,
          TWEAK_BOUNDS.dmgHi * 100,
          5,
          Math.round(cur * 100),
          (v) => `${v}%`,
          (v) => {
            const mul = v / 100;
            if (Math.abs(mul - 1) < 1e-9) delete this.tweaks.dmg?.[id];
            else (this.tweaks.dmg ??= {})[id] = mul;
            if (this.tweaks.dmg && !Object.keys(this.tweaks.dmg).length) delete this.tweaks.dmg;
            save();
          },
        ),
      );
    }

    const knob = (k: ScalarKnob, label: string, lo: number, hi: number, fmt: (v: number) => string): HTMLElement =>
      sliderRow(label, lo, hi, 1, this.tweaks[k] ?? TWEAK_DEFAULTS[k], fmt, (v) =>
        setScalar(this.tweaks, k, v, TWEAK_DEFAULTS[k], save),
      );

    const timerList = el('div', 'display:flex;flex-direction:column;gap:6px');
    timerList.append(
      knob('chiliRounds', 'Chili burn — rounds afire', TWEAK_BOUNDS.roundsLo, TWEAK_BOUNDS.roundsHi, (v) => `${v} round${v === 1 ? '' : 's'}`),
      knob('lemonRounds', 'Lemon acid — rounds stinging', TWEAK_BOUNDS.roundsLo, TWEAK_BOUNDS.roundsHi, (v) => `${v} round${v === 1 ? '' : 's'}`),
      knob('beeLifeS', 'Beehive swarm — lifetime', TWEAK_BOUNDS.beeLifeLo, TWEAK_BOUNDS.beeLifeHi, (v) => `${v}s`),
      knob('beeStingDmg', 'Bee sting — damage per hit', TWEAK_BOUNDS.stingLo, TWEAK_BOUNDS.stingHi, (v) => `${v} dmg`),
    );

    // Movement — jump is THE verb of this game, so its power gets a knob too.
    const moveList = el('div', 'display:flex;flex-direction:column;gap:6px');
    moveList.append(
      sliderRow(
        'Jump power — launch speed',
        TWEAK_BOUNDS.jumpLo * 100,
        TWEAK_BOUNDS.jumpHi * 100,
        5,
        Math.round((this.tweaks.jumpPower ?? TWEAK_DEFAULTS.jumpPower) * 100),
        (v) => `${v}%`,
        (v) => {
          const mul = v / 100;
          if (Math.abs(mul - TWEAK_DEFAULTS.jumpPower) < 1e-9) delete this.tweaks.jumpPower;
          else this.tweaks.jumpPower = mul;
          save();
        },
      ),
    );

    const cols = el('div', 'display:flex;gap:10px;width:100%;margin-top:8px;align-items:flex-start');
    const left = el('div', 'flex:0 0 44%;display:flex;flex-direction:column;gap:6px;min-width:0');
    const right = el('div', 'flex:1 1 56%;display:flex;flex-direction:column;gap:6px;min-width:0');
    left.append(sectionLabel('Ammo per weapon', true), ammoList);
    right.append(
      sectionLabel('Power · damage per weapon', true), powerList,
      sectionLabel('Effect timers'), timerList,
      sectionLabel('Movement'), moveList,
    );
    cols.append(left, right);

    const actions = el('div', 'display:flex;gap:8px;width:100%;margin-top:6px');
    actions.append(
      mkBtn('↺ Reset to stock', ROW_BTN + ';text-align:center;font-size:13px', () => {
        this.tweaks = {};
        if (this.setupFromRoom) this.pushRoomTweaks(true); // {} clears the room's tuning
        else saveWeaponTweaks(undefined);
        this.renderSetup();
      }),
      mkBtn('← Back', ROW_BTN + ';text-align:center;font-size:13px', () => {
        if (this.setupFromRoom) {
          this.pushRoomTweaks(true); // flush any pending slider drag
          this.setupFromRoom = false;
          this.show('room');
        } else {
          this.show('menu');
        }
      }),
    );

    this.setupEl.append(
      sectionLabel('Map', true), mapList,
      sectionLabel('Soldiers'), healthList,
      cols,
      sectionLabel('Aiming'), aimList,
      actions,
    );
  }

  /**
   * The saved setup as a full match config — the single source `Create room`
   * sends. `random` is resolved here (a room is created once, so the map has
   * to be concrete by then); the room screen can still change it afterwards.
   */
  private buildConfig(): PublicMatchConfig {
    const theme: ThemeId =
      this.mapChoice === 'random' ? THEME_IDS[Math.floor(Math.random() * THEME_IDS.length)] : this.mapChoice;
    const tweaks = clampTweaks(this.tweaks);
    return {
      ...DEFAULT_CONFIG,
      theme,
      // The host's aim-line pick ships with the room: everyone in it plays
      // under the same preview, whatever their own setting says.
      aimLine: loadAimLine(),
      ...(tweaks ? { weaponTweaks: tweaks } : {}),
    };
  }

  /**
   * The code field as a room code: `''` (create picks one), a valid code, or
   * `null` after showing why the typed text isn't one. Tolerates a pasted
   * `JOIN-ABCD` — the prefix is an old habit worth keeping working.
   */
  private roomCodeAttempt(): string | null {
    const raw = this.codeInput.value.trim().toUpperCase().replace(/^JOIN[-\s]*/, '');
    if (!raw) return '';
    const code = normalizeRoomCode(raw);
    if (!code) {
      this.showError('Room codes are 4 characters — letters (no I/O) and 2–9.');
      return null;
    }
    this.codeInput.value = code; // leave the field showing the real code
    return code;
  }

  /** The control guide — this text used to be pinned above the screen. */
  private buildGuide(): void {
    const h = (text: string): HTMLElement =>
      el('h2', 'margin:6px 0 0;font-size:22px;letter-spacing:.03em;align-self:center', text);
    const sec = (title: string, ...lines: string[]): HTMLElement => {
      const box = el(
        'div',
        'padding:11px 13px;border:1px solid rgba(255,255,255,.16);border-radius:11px;background:rgba(255,255,255,.05)',
      );
      box.append(el('div', 'font:700 13px system-ui;color:#ffd76a;margin-bottom:5px', title));
      for (const line of lines) {
        box.append(el('div', 'font:13px/1.55 system-ui;opacity:.88;margin:2px 0', line));
      }
      return box;
    };

    this.guideEl.append(
      h('How to play'),
      sec(
        'Goal',
        'Blast the enemy squad off the map — last team standing wins.',
        'Your 3 soldiers take turns; rounds alternate between teams, then walk down your squad.',
      ),
      sec(
        'Your turn',
        'Jump once · fire once (two more shots possible mid-air) · swap once per match · pass when done.',
        'Firing ends the turn after everything settles. A burning soldier sits out their round.',
      ),
      sec(
        'Touch controls',
        'Tap a toolbar icon, then drag on the field and release — the drag aims.',
        'Weapons are slingshots: pull back the opposite way, let go.',
        'Jump fires you like a cannon — aim ANY angle (straight up too), pull further for more speed.',
        'Jump + a weapon stack: tap Jump, tap a fruit — the jump takes the first drag, the fruit the next.',
        'Jump keeps your turn — drag mid-air and the world crawls in slow-mo; release to fire.',
        'Two-finger drag pans · pinch zooms.',
      ),
      sec(
        'Keyboard (desktop)',
        'Drag to aim · release or Enter fires · 1-9, Q/E pick a weapon.',
        'J jump · X swap · P pass · W/Space jump to the mouse · Esc menu.',
        'Arrows or middle-drag pan · wheel zooms · C carve terrain · R new seed · T next map.',
      ),
      sec(
        'World rules',
        'Craters are forever — terrain never grows back.',
        'Jump landings never hurt — you always bounce a little. Blasts still shove you off cliffs.',
        'Water drowns a soldier in 3 s · wind drifts every shot (and your jump).',
        'Dead soldiers keep tumbling — gravity and blasts move the body, not just the living.',
      ),
      sec(
        'Aim line',
        'Setup & tuning → “Aim line”: Normal (the default) or Extended.',
        'Extended draws the full arc and the landing ring; Normal stops the dots ~2/3 out — you read the shot.',
        'Beans: the burst walks onto your predicted landing; drag mid-burst to redirect the rest.',
      ),
      sec(
        'Setup & tuning',
        'Menu → “Setup & tuning”: map · starting health · aim line · ammo · power & jump · room code.',
        'Ammo count wraps: past 0 comes ∞ (unlimited), and the 20 cap steps up to ∞ too.',
        'Power scales damage · timers stretch chili/lemon/bees · jump sets your launch power.',
        'Saved as you go — Play vs CPU and rooms you create use it; Find match uses the server’s rules.',
      ),
      mkBtn('← Back', ROW_BTN + ';text-align:center', () => this.show('menu')),
    );
  }

  private squadLabel(): string {
    const names = this.squad.map((s) => CHARACTERS[s]?.name ?? s);
    return names.length ? `👥 Squad: ${names.join(', ')}` : '👥 Squad: pick your soldiers';
  }

  /** Tap a species to add it; a 4th pick pushes the oldest one out. */
  private toggleSquad(id: SpeciesId): void {
    const i = this.squad.indexOf(id);
    if (i >= 0) this.squad.splice(i, 1);
    else {
      this.squad.push(id);
      if (this.squad.length > SQUAD_SIZE) this.squad.shift();
    }
    saveSquad(this.squad);
    this.renderSquad();
    this.squadBtn.textContent = this.squadLabel();
  }

  private renderSquad(): void {
    this.squadEl.replaceChildren();
    this.squadEl.append(
      el('h2', 'margin:10px 0 0;font-size:22px;letter-spacing:.03em', 'Choose your squad'),
      el('p', 'margin:0;opacity:.65;font-size:13px;text-align:center;max-width:36ch',
        `Tap ${SQUAD_SIZE} soldiers — they fight together as one team. Tap again to remove.`),
    );

    // --- current picks: real art cards, in pick order ------------------------
    const picks = el('div', 'display:flex;gap:8px;justify-content:center;margin:6px 0 2px');
    for (let i = 0; i < SQUAD_SIZE; i++) {
      const id = this.squad[i];
      const slot = el(
        'div',
        'position:relative;display:flex;flex-direction:column;align-items:center;gap:2px;' +
          'width:104px;padding:10px 6px 8px;border-radius:14px;box-sizing:border-box;' +
          `border:2px solid ${id ? '#ffd76a' : 'rgba(255,255,255,.22)'};` +
          `background:${id ? 'linear-gradient(180deg,rgba(255,215,106,.16),rgba(255,215,106,.05))' : 'rgba(255,255,255,.04)'}`,
      );
      // Pick-order badge.
      slot.append(el('div',
        'position:absolute;top:5px;left:7px;font:800 10px system-ui;color:#ffd76a;opacity:.9',
        `#${i + 1}`));
      if (id && CHARACTERS[id]) {
        const spec = CHARACTERS[id];
        const cv = portrait(id, 62);
        const rm = el('button',
          'position:absolute;top:3px;right:5px;width:20px;height:20px;padding:0;border:none;' +
          'border-radius:50%;background:rgba(0,0,0,.45);color:#fff;font:700 12px/1 system-ui;cursor:pointer');
        rm.type = 'button';
        rm.textContent = '×';
        rm.title = `Remove ${spec.name}`;
        rm.addEventListener('click', () => {
          this.toggleSquad(id);
        });
        slot.append(cv, rm, el('span', 'font:700 12px system-ui', spec.name));
        // Characters are skins — the line under the name is flavor, not stats.
        slot.append(el('span',
          'font:600 10px/1.3 system-ui;opacity:.65;text-align:center;padding:0 2px',
          CHARACTER_BLURBS[id]));
      } else {
        const empty = el('div',
          'width:62px;height:62px;border-radius:12px;border:2px dashed rgba(255,255,255,.3);' +
          'display:flex;align-items:center;justify-content:center;font-size:22px;opacity:.5', '+');
        slot.append(empty, el('span', 'font:600 12px system-ui;opacity:.45', 'empty'));
      }
      picks.append(slot);
    }
    this.squadEl.append(picks);

    // Quick actions under the cards.
    const actions = el('div', 'display:flex;gap:8px;width:min(300px,90vw)');
    actions.append(
      mkBtn('🎲 Random squad', ROW_BTN + ';text-align:center;font-size:13px', () => {
        const pool = [...SPECIES_ORDER];
        const pick: SpeciesId[] = [];
        for (let i = 0; i < SQUAD_SIZE && pool.length; i++) {
          const j = Math.floor(Math.random() * pool.length);
          pick.push(pool.splice(j, 1)[0]);
        }
        this.squad = pick;
        saveSquad(this.squad);
        this.renderSquad();
        this.squadBtn.textContent = this.squadLabel();
      }),
      mkBtn('← Back', ROW_BTN + ';text-align:center;font-size:13px', () => this.show('menu')),
    );
    this.squadEl.append(actions);

    this.squadEl.append(el('div',
      'font:700 11px system-ui;opacity:.55;letter-spacing:.14em;text-transform:uppercase;margin-top:8px',
      `Roster · ${SPECIES_ORDER.length} characters`));

    // --- full roster grid: every soldier shown as real art -------------------
    const grid = el(
      'div',
      'display:flex;flex-wrap:wrap;gap:8px;justify-content:center;max-height:40vh;overflow-y:auto;' +
        'padding:6px;box-sizing:border-box;width:100%',
    );
    for (const id of SPECIES_ORDER) {
      const spec = CHARACTERS[id];
      if (!spec) continue;
      const pickIdx = this.squad.indexOf(id);
      const on = pickIdx >= 0;
      const b = document.createElement('button');
      b.type = 'button';
      b.title = `${spec.name} — ${CHARACTER_BLURBS[id]}`;
      b.style.cssText =
        'position:relative;display:flex;flex-direction:column;align-items:center;gap:3px;width:76px;' +
        'padding:8px 4px 6px;box-sizing:border-box;' +
        `border:2px solid ${on ? '#ffd76a' : 'rgba(255,255,255,.18)'};border-radius:12px;` +
        `background:${on ? 'linear-gradient(180deg,rgba(255,215,106,.2),rgba(255,215,106,.06))' : 'rgba(255,255,255,.05)'};` +
        'color:#fff;cursor:pointer;touch-action:manipulation;font:600 11px/1.2 system-ui';
      b.append(portrait(id, 54));
      b.append(el('span',
        'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:68px', spec.name));
      if (on) {
        // Pick-order chip instead of a plain checkmark.
        b.append(el('span',
          'position:absolute;top:4px;right:4px;min-width:16px;height:16px;border-radius:8px;' +
          'background:#ffd76a;color:#20160a;font:800 10px/16px system-ui;text-align:center',
          String(pickIdx + 1)));
      }
      b.addEventListener('click', () => {
        this.toggleSquad(id);
        b.blur();
      });
      grid.append(b);
    }
    this.squadEl.append(grid);
  }

  /** Open the online screen and make sure a socket is on its way up. */
  private openOnline(): void {
    this.show('online');
    this.cbs.onOpenOnline(this.nameInput.value.trim() || 'player');
    // No focus(): the name box lives on the menu now — this screen is codes.
  }

  private tryJoin(): void {
    if (!this.online) return this.showError('Connecting… try again in a moment.');
    const code = this.roomCodeAttempt();
    if (code === null) return;
    if (!code) return this.showError('Room codes are 4 characters — letters (no I/O) and 2–9.');
    this.clearError();
    this.cbs.onJoin(code);
  }

  private renderRoom(): void {
    const room = this.room;
    if (!room) return;
    this.roomEl.replaceChildren();

    // Instant rooms have no code to share and no host to configure: the server
    // config already decided the map, the rules and the roster. All this screen
    // does is count down and show who is about to fight.
    if (room.kind === 'instant') {
      this.renderInstantRoom(room);
      return;
    }

    this.roomEl.append(
      el('div', 'font:700 12px system-ui;opacity:.6;letter-spacing:.1em;text-transform:uppercase', 'Room'),
    );

    // Big tap-to-copy code.
    const codeBtn = mkBtn(
      room.code,
      'font:800 clamp(28px,10vw,40px)/1.1 system-ui;letter-spacing:.24em;padding:12px 0;width:100%;' +
        'border:1px dashed rgba(255,255,255,.4);border-radius:12px;background:rgba(255,255,255,.06);color:#ffd76a;' +
        'cursor:pointer;touch-action:manipulation',
      () => {
        void navigator.clipboard?.writeText(room.code).catch(() => undefined);
        this.showError(`Copied ${room.code}`);
        window.setTimeout(() => this.clearError(), 1200);
      },
    );
    this.roomEl.append(codeBtn);
    this.roomEl.append(el('div', 'font:11px system-ui;opacity:.55;text-align:center;margin-top:-4px', 'tap to copy · share with a friend'));

    // Players.
    const list = el('div', 'display:flex;flex-direction:column;gap:6px;margin-top:6px');
    for (const p of room.players) {
      const isHost = p.playerId === room.hostId;
      const isMe = p.playerId === this.myId;
      const row = el(
        'div',
        'display:flex;align-items:center;gap:8px;padding:9px 11px;border-radius:9px;' +
          `background:rgba(255,255,255,${isMe ? 0.12 : 0.06});font:14px system-ui`,
      );
      row.append(el('span', 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap', `${p.name}${isMe ? ' (you)' : ''}`));
      if (p.squad?.length) {
        const sw = el('span', 'display:flex;gap:3px;align-items:center');
        for (const sid of p.squad.slice(0, SQUAD_SIZE)) {
          const spec = CHARACTERS[sid];
          if (!spec) continue;
          const dot = el('span', 'width:12px;height:12px;border-radius:50%;display:inline-block');
          dot.style.background = spec.palette.body;
          dot.style.boxShadow = `inset -3px -3px 0 ${spec.palette.shade}`;
          sw.append(dot);
        }
        row.append(sw);
      }
      if (isHost) row.append(el('span', 'font-size:12px;opacity:.7', 'host'));
      row.append(el('span', p.ready ? 'color:#7ee08a;font-weight:700' : 'opacity:.45', p.ready ? 'ready' : 'waiting'));
      list.append(row);
    }
    // A friend room is one squad against one squad — one empty seat at most —
    // and an empty one is never quietly handed to a CPU, so the host just waits.
    const missing = Math.max(0, room.config.seats - room.players.length);
    this.roomEl.append(list);

    // Host-only setup row: the WHOLE Setup & tuning screen, so the map, ammo,
    // weapon powers and everything else can be changed between rounds — this
    // room's config, pushed live so the friend's screen follows.
    const isHost = room.hostId === this.myId;
    if (isHost) {
      this.roomEl.append(
        mkBtn('⚙  Setup & tuning   ›', ROW_BTN, () => this.openSetupFromRoom()),
      );
    }
    this.roomEl.append(
      el('div', 'font:12px system-ui;opacity:.6;text-align:center;padding:4px 0',
        `theme ${room.config.theme} · 1 vs 1`),
    );

    this.roomEl.append(
      el('div', 'font:11px/1.45 system-ui;opacity:.6;text-align:center',
        missing > 0
          ? 'Waiting for your friend to join — share the code above. No CPUs here, ever.'
          : 'Both here — ready up to start.'),
    );

    // Ready + leave.
    const me = room.players.find((p) => p.playerId === this.myId);
    const ready = !!me?.ready;
    const readyBtn = mkBtn(ready ? '✓  Ready — waiting for others' : 'Ready up', ROW_BTN, () => {
      // Our tap may be what starts the match — spend the gesture on
      // fullscreen now, while the browser still counts it as user input.
      enterFullscreenIfUseful();
      this.cbs.onReady(!ready);
    });
    if (ready) {
      readyBtn.style.background = 'rgba(126,224,138,.18)';
      readyBtn.style.borderColor = 'rgba(126,224,138,.6)';
    }
    this.roomEl.append(readyBtn);
    this.roomEl.append(
      mkBtn('Leave room', ROW_BTN, () => {
        this.cbs.onLeave();
        this.clearRoom();
      }),
    );
  }

  private renderInstantRoom(room: RoomStateMsg): void {
    // A stranger match is humans only — the queue never seats a CPU.
    const roster = room.roster ?? [];
    this.roomEl.append(
      el(
        'div',
        'font:700 12px system-ui;opacity:.6;letter-spacing:.1em;text-transform:uppercase',
        '⚡ Find match',
      ),
    );

    const secs = Math.max(0, Math.ceil(room.countdownS ?? 0));
    const rosterHumans = roster.filter((s) => s.human).length;
    const matched = rosterHumans || room.players.length;
    this.roomEl.append(
      el('h2', 'margin:2px 0 0;font-size:clamp(24px,8vw,32px);letter-spacing:.04em',
        secs > 0 ? `${secs}s` : 'Starting…'),
    );
    this.roomEl.append(
      el('div', 'font:12px/1.4 system-ui;opacity:.7;text-align:center',
        `${matched} human${matched === 1 ? '' : 's'} matched · ${room.config.seats} seats`),
    );

    // Seat-by-seat roster from the server.
    const list = el('div', 'display:flex;flex-direction:column;gap:6px;margin-top:8px;width:100%');
    for (const [i, s] of roster.entries()) {
      const row = el(
        'div',
        'display:flex;align-items:center;gap:8px;padding:9px 11px;border-radius:9px;' +
          `background:rgba(255,255,255,${s.human ? 0.12 : 0.05});font:14px system-ui`,
      );
      row.append(el('span', 'opacity:.5;width:52px;font-size:12px', `Seat ${i + 1}`));
      row.append(el('span', 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap', s.name));
      row.append(el('span', s.human ? 'color:#9fd8ff;font-size:12px' : 'opacity:.45;font-size:12px', s.human ? 'human' : 'CPU'));
      list.append(row);
    }
    this.roomEl.append(list);

    const cfg = room.config;
    this.roomEl.append(
      el('div', 'font:11px/1.45 system-ui;opacity:.55;text-align:center;margin-top:4px',
        `theme ${cfg.theme} · ${cfg.mode === 'ffa' ? 'free-for-all' : `${cfg.teamSize}-soldier squads`}`),
    );

    this.roomEl.append(
      mkBtn('✕  Leave match', ROW_BTN + ';text-align:center;margin-top:6px', () => {
        this.cbs.onLeave();
        this.clearRoom();
      }),
    );
  }
}
